import _ from "lodash";
import store, { getStoreEntity } from "services/store";
import fetchers from "./services";
import { getPackValuesWithPresetsComment } from "utils/parsers";
import { PackSchema } from "utils/schemas";
import { UBUNTU_ADVANTAGE_SCHEMAS } from "utils/constants/schemas";
import { getUbuntuAdvantagePresets } from "utils/yaml";
import { presentUniquePackName } from "utils/presenters";
import { PARAM_SEPARATOR } from "utils/editor/formMode";
import { FORM_TYPES } from "./services";

export function isBasicLayer(type) {
  return ["os", "cni", "csi", "k8s"].includes(type);
}

export function isPacksSearchAvailable(layer) {
  if (!layer) {
    return false;
  }

  const { formType, type, defaultPackType = "", accessType } = layer;
  const packType = type === "new" ? defaultPackType : type;

  const disabledPackTypes = ["zarf"];

  if (accessType === "private") {
    disabledPackTypes.push(defaultPackType);
  }

  if (formType !== FORM_TYPES.PACK) {
    return false;
  }

  if (disabledPackTypes.includes(packType)) {
    return false;
  }

  if (isBasicLayer(type)) {
    return false;
  }

  return true;
}

export function generateUbuntuAdvantagePresets(data) {
  if (!data) {
    return;
  }
  if (!data.enabled) {
    return [
      getUbuntuAdvantagePresets({ field: "enabled", value: false }, data),
    ];
  }
  return Object.keys(data).map((field) =>
    getUbuntuAdvantagePresets({ field, value: data[field] }, data)
  );
}

export function mapFormDataToLayers(formData = {}) {
  if (formData.type === "manifest") {
    const { type, persisted, ...rest } = formData;
    return {
      type,
      config: {
        ...rest,
      },
    };
  }

  const {
    packType,
    version,
    pack,
    repository,
    values,
    packUid,
    presets,
    manifests,
    installOrder,
    displayName,
    isOci,
    isIntegration,
    name,
    logo,
    annotations,
  } = formData;
  const packVersions = fetchers.packVersionsFetcher.selector(
    store.getState()
  )?.result;

  const selectedPackVersion = packVersions?.find(
    (pack) => pack.tag === version
  );
  const packGuid = selectedPackVersion?.pack?.guid;
  const packValues = getStoreEntity(packGuid, PackSchema);
  const packName = formData.packType === "manifest" ? name : pack || name;

  const integrationConfig = Object.entries(formData).reduce(
    (accumulator, [key, value]) => {
      if (key.startsWith(PARAM_SEPARATOR)) {
        accumulator[key] = value;
      }

      return accumulator;
    },
    {}
  );

  return {
    type: packType,
    annotations,
    config: {
      registryUid: repository,
      name: packName,
      tag: version,
      isOci,
      isIntegration,
      values,
      packUid,
      logo: selectedPackVersion?.logoUrl || logo,
      presets: packValues?.spec?.presets,
      template: packValues?.spec?.template,
      ubuntuAdvantagePresets: generateUbuntuAdvantagePresets(
        presets?.ubuntuAdvantage
      ),
      schema: [
        ...(packValues?.spec?.schema || formData.schema || []),
        ...(presets?.ubuntuAdvantage?.enabled ? UBUNTU_ADVANTAGE_SCHEMAS : []),
      ],
      packVersionGuid: selectedPackVersion?.guid,
      selectedPresets: presets,
      installOrder,
      displayName,
      manifests: (manifests || []).filter((manifest) => !!manifest?.name),
      ...integrationConfig,
    },
  };
}

export function extractPayload(layers = []) {
  return layers.reduce((acc, layer) => {
    let type = "spectro";
    const config = layer.config;
    const packName = presentUniquePackName(config);

    if (config) {
      let manifests = config?.manifests
        ?.filter((manifest) => (manifest.uid ? true : !!manifest?.name))
        .map((manifest) => ({
          // On layer editing: if we already have some manifests and change the pack name,
          // we cannot use the manifest uids as they are linked to the old pack name
          uid: packName === layer.guid ? manifest.uid : undefined,
          name: manifest?.name,
          content: manifest?.content,
        }));

      let values = getPackValuesWithPresetsComment(
        layer.config.values,
        layer.config.selectedPresets
      );

      if (layer.type === "manifest") {
        type = "manifest";

        return [
          ...acc,
          {
            installOrder: layer.config.installOrder || "0",
            type,
            name: layer.config.name,
            uid: layer.config.packUid || "spectro-manifest-pack",
            packUid: layer.config.packUid,
            manifests,
            values,
          },
        ];
      }

      if (layer.type === "helmChart") {
        type = "helm";
      }

      if (layer.type === "zarf" || layer.accessType === "private") {
        type = "oci";
      }

      if (config.isOci) {
        type = "oci";
      }

      return [
        ...acc,
        {
          installOrder: isBasicLayer(layer.type)
            ? undefined
            : layer.installOrder || "0",
          layer: config.isOci ? "addon" : config.layer,
          ..._.omit(config, [
            "presets",
            "schema",
            "packVersionGuid",
            "selectedPresets",
            "isOci",
            "isIntegration",
            "ubuntuAdvantagePresets",
            "displayName",
          ]),
          type,
          uid: layer.config.packUid || "",
          name: packName,
          values,
          manifests,
        },
      ];
    }

    return acc;
  }, []);
}
